import { useState } from "react";
import { X, Bot, FileText } from "lucide-react";
import { Rnd } from "react-rnd";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import { getAgentPageResult } from "../../../api/const";

const typingCSS = `
.typing span {
  display: inline-block;
  animation: blink 1.4s infinite both;
}
.typing span:nth-child(2) { animation-delay: 0.2s; }
.typing span:nth-child(3) { animation-delay: 0.4s; }

@keyframes blink {
  0% { opacity: .2; }
  20% { opacity: 1; }
  100% { opacity: .2; }
}
`;
const AgentPage = ({ onClose }) => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState("");
  const [error, setError] = useState("");

  const handleAnalyze = async () => {
    setLoading(true);
    setError("");
    setResult("");

    try {
      let text = document.body.innerText?.trim() || "";

      // 분석 UI 문구 제거
      const removeList = [
        "페이지 분석",
        "아래 버튼을 눌러 이 페이지 내용을 분석하세요.",
        "현재 페이지 분석하기",
        "분석 중...",
      ];
      removeList.forEach((p) => (text = text.replace(new RegExp(p, "g"), "")));

      if (text.length > 20000) text = text.slice(0, 20000);

      if (!text) {
        setError("⚠️ 분석할 텍스트가 없습니다.");
        return;
      }

      const res = await getAgentPageResult({ page: text });
      setResult(typeof res === "string" ? res : res?.result || "");
    } catch (err) {
      console.error(err);
      setError("⚠️ 분석 중 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{typingCSS}</style>
      <Rnd
        default={{
          x: window.innerWidth - 650,
          y: window.innerHeight - 550,
          width: 520,
          height: 420,
        }}
        minWidth={420}
        minHeight={360}
        bounds="window"
        dragHandleClassName="agentpage-drag"
        className="z-[9999]"
      >
        <div className="bg-white border rounded-xl shadow-2xl flex flex-col overflow-hidden h-full">

          {/* 헤더 = 챗봇 동일 */}
          <div
            className="agentpage-drag flex items-center justify-between px-4 py-3 text-white"
            style={{ background: "linear-gradient(90deg, #1A1B2E 0%, #262742 100%)" }}
          >
            <div className="flex items-center space-x-2">
              <div className="w-7 h-7 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg flex items-center justify-center">
                <FileText size={16} className="text-[#1A1B2E]" />
              </div>
              <span className="font-semibold text-sm">페이지 분석</span>
            </div>

            {onClose && (
              <button onClick={onClose} className="hover:bg-blue-700/30 p-1 rounded-md">
                <X size={18} />
              </button>
            )}
          </div>

          {/* 분석 내용 영역 (챗봇 메시지 스타일 1개 버전) */}
          <div className="p-4 flex-1 overflow-y-auto text-sm space-y-3 bg-gray-50">

            {!loading && !result && !error && (
              <p className="text-gray-400 text-center">
                아래 버튼을 눌러 이 페이지 내용을 분석하세요.
              </p>
            )}

            {loading && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg shadow-sm max-w-[90%] bg-white border text-gray-800">
                  <div className="flex items-center mb-1 text-blue-500">
                    <Bot size={14} className="mr-1" />
                    <span className="text-xs font-semibold">AI</span>
                  </div>

                  <div className="typing text-gray-500 text-xs">
                    <span>●</span><span>●</span><span>●</span>
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div className="text-red-500 font-medium text-center">{error}</div>
            )}

            {result && (
              <div className="flex justify-start">
                <div className="px-3 py-2 rounded-lg shadow-sm max-w-[90%] bg-white border text-gray-800">
                  <div className="flex items-center mb-1 text-[#1A1B2E]">
                    <Bot size={14} className="mr-1" />
                    <span className="text-xs font-semibold">AI</span>
                  </div>

                  <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={{
                      ul({ children, ...props }) {
                        return (
                          <ul className="list-disc list-inside pl-4" {...props}>
                            {children}
                          </ul>
                        );
                      },

                      li({ children, ...props }) {
                        return (
                          <li className="my-1" {...props}>
                            {children}
                          </li>
                        );
                      },

                      code({ node, inline, className, children, ...props }) {
                        const match = /language-(\w+)/.exec(className || "");

                        if (!inline && match) {
                          return (
                            <SyntaxHighlighter
                              {...props}
                              PreTag="div"
                              language={match[1]}
                              style={oneDark}
                            >
                              {String(children).replace(/\n$/, "")}
                            </SyntaxHighlighter>
                          );
                        }

                        return (
                          <code className="bg-gray-200 px-1 py-0.5 rounded" {...props}>
                            {children}
                          </code>
                        );
                      }
                    }}
                  >
                    {String(result)}
                  </ReactMarkdown>
                </div>
              </div>
            )}
          </div>

          {/* 버튼 영역 */}
          <div className="border-t p-3 bg-white">
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="w-full text-white py-2 rounded-lg font-medium disabled:opacity-50"
              style={{ backgroundColor: "#1A1B2E" }}
            >
              {loading ? "분석 중..." : "현재 페이지 분석하기"}
            </button>
          </div>
        </div>
      </Rnd>
    </>
  );
};

export default AgentPage;
